import { ApiError } from '@l2u/ui/lib/api.ts';
import { isMfaRequiredError, type GateDecision } from '@l2u/ui/lib/mfa.ts';

// The gate's decisions, kept pure so they are testable without a DOM
// (this monorepo has no jsdom — spec §8). VendorMfaGate.tsx is the wiring.

export type GateView = 'spinner' | 'children' | 'error' | 'enrol' | 'step-up';

/**
 * What the gate should render.
 *
 * Order matters and is the whole point:
 *
 *  1. `authLoading` wins outright — until the session is restored, hasSession
 *     is not yet meaningful and acting on it would flash the wrong screen.
 *  2. No session hands straight through to the shell, which owns the redirect
 *     to /login (and preserves search/hash doing it). Rendering an enrolment
 *     screen to a signed-out visitor would ask a stranger to set up 2FA.
 *  3. Unknown factor state waits, and a FAILED read shows an error rather than
 *     falling back to a decision. Treating "could not read" as "not enrolled"
 *     would invite an already-enrolled vendor who is merely offline to add a
 *     second factor on top of the one they hold (D15).
 */
export function gateView(input: {
  authLoading: boolean;
  hasSession: boolean;
  mfaLoading: boolean;
  mfaError: Error | null;
  decision: GateDecision;
}): GateView {
  if (input.authLoading) return 'spinner';
  if (!input.hasSession) return 'children';
  if (input.mfaLoading) return 'spinner';
  if (input.mfaError) return 'error';
  if (input.decision === 'enrol') return 'enrol';
  if (input.decision === 'step-up') return 'step-up';
  return 'children';
}

/**
 * How CompanyShell should react to an API error.
 *
 * The `refresh` case is the one that matters. companyAuth answers 403
 * `mfa_required` when the bearer token is still aal1 — which happens
 * legitimately after an admin resets the vendor's factors mid-session, or when
 * a tab holds a token minted before the step-up. Signing out there would cost
 * the vendor a whole new emailed magic link for what is really a stale token.
 * Refreshing rotates it, and the gate — keyed on the access token (D8) —
 * re-evaluates and shows the right screen.
 *
 * An ordinary 403 must still sign out, or a deactivated vendor would sit in a
 * refresh loop. A 500 is not an auth problem at all and must not cost anyone
 * their session.
 */
export function shellAuthErrorAction(err: unknown): 'refresh' | 'sign-out' | 'none' {
  if (isMfaRequiredError(err)) return 'refresh';
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) return 'sign-out';
  return 'none';
}
